<script setup lang="ts">
import { defineAsyncComponent, onDeactivated, toRef } from 'vue'
import { useEnterpriseImageResult } from '~/composables/useEnterpriseImageResult'
import ImageResultCard from './ImageResultCard.vue'

const props = defineProps<{ recordId: number; showFilename?: boolean }>()
const emit = defineEmits<{ (event: 'saved'): void }>()
const Editor = defineAsyncComponent(() => import('./SampleOverlayDialog.vue'))
const result = useEnterpriseImageResult(toRef(props, 'recordId'))
const { sourceUrl, resultUrl, filename, loading, busy, errorKey, canView, canEdit, canDownload, editorVisible, editorMode } = result
onDeactivated(result.suspend)
async function save(payload: { pngBlob: Blob }) {
  await result.save(payload.pngBlob)
  if (!errorKey.value && !editorVisible.value && canView.value) emit('saved')
}
</script>

<template>
  <div class="enterprise-image-result" data-testid="enterprise-image-result">
    <h1 v-if="showFilename && canView" class="result-filename" :title="filename">{{ filename }}</h1>
    <p v-if="loading" class="result-message" role="status">{{ $t('bizImage_loading') }}</p>
    <div v-if="errorKey && !editorVisible" class="result-error" role="alert">
      <span>{{ $t(errorKey) }}</span>
      <button type="button" :disabled="busy || loading" @click="result.load()">{{ $t('bizImage_retry') }}</button>
    </div>
    <ImageResultCard v-if="canView" :source-url="sourceUrl" :result-url="resultUrl" :can-edit="canEdit" :can-download="canDownload" :busy="busy || loading" @edit="result.openEditor" @download="result.download" />
    <Teleport to="body">
      <Editor v-if="editorVisible" v-model:visible="editorVisible" :source-url="sourceUrl" :target-url="resultUrl" :initial-mode="editorMode" :filename="filename" :processing="busy" :error-message="errorKey ? $t(errorKey) : ''" @apply="save" />
    </Teleport>
  </div>
</template>

<style scoped>
.enterprise-image-result { min-width: 0; }
.result-filename { margin: 0 0 18px; color: #1f2329; font-size: 18px; line-height: 28px; font-weight: 700; overflow-wrap: anywhere; }
.result-message, .result-error { margin: 0 0 12px; padding: 16px; border: 1px solid #ebeef2; border-radius: 14px; background: #fff; color: #8f959e; font-size: 13px; line-height: 22px; }
.result-error { display: flex; gap: 12px; align-items: center; color: #d75b65; }
.result-error button { flex-shrink: 0; border: 1px solid #d9dee5; border-radius: 8px; background: #fff; color: #2b7de9; padding: 4px 12px; font: inherit; cursor: pointer; }
.result-error button:disabled { color: #a6adb7; cursor: not-allowed; }
</style>
